import type { DefectSeverity } from '@carcheck/shared';
import Chip, { type ChipProps } from '@mui/material/Chip';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Inspection defect severity badge (AUT-151). Renders the 3-level scale
 * as a coloured MUI Chip — colours come from the theme palette, NOT raw
 * hex (CLAUDE.md rule #1 / no magic literals):
 *
 *   - minor    → `warning`         (amber)  — mockup `.sev-badge--minor`
 *   - medium   → `severityMedium`  (orange) — custom palette slot added
 *                                             in `design/theme.ts`
 *   - critical → `error`           (red)    — mockup `.sev-badge--critical`
 *
 * Typed as a total `Record<DefectSeverity, …>` so a change to the
 * `DEFECT_SEVERITIES` union in `@carcheck/shared` fails typecheck here
 * rather than rendering an uncoloured chip at runtime. Labels resolve
 * through i18n (`reports.defects.severity.<value>`; rule #11).
 */
type ChipColor = ChipProps['color'];

const SEVERITY_COLOR: Record<DefectSeverity, ChipColor> = {
  minor: 'warning',
  medium: 'severityMedium',
  critical: 'error',
};

export const DefectSeverityBadge = ({ severity }: { severity: DefectSeverity }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      color={SEVERITY_COLOR[severity]}
      label={t(`reports.defects.severity.${severity}`)}
    />
  );
};
